const TERMINAL_CHECKPOINT_VERSION = 1 as const;
const TERMINAL_CHECKPOINT_IV_BYTES = 12;
const TERMINAL_CHECKPOINT_STORAGE_PREFIX =
  'privacy-pools:terminal-checkpoint:v1:';
const TERMINAL_OWNER_NAMESPACE = 'privacy-pools:terminal-owner:v1:';
const TERMINAL_KEY_NAMESPACE = 'privacy-pools:terminal-key:v1:';
const TERMINAL_HKDF_SALT = 'privacy-pools:terminal-checkpoint:salt:v1';
const TERMINAL_HKDF_INFO = 'privacy-pools:terminal-checkpoint:data-key:v1';
export const TERMINAL_CHECKPOINT_ACCOUNT_MARKER = '__terminalCheckpoint';

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

type TerminalCheckpointEnvelopeV1 = {
  version: typeof TERMINAL_CHECKPOINT_VERSION;
  iv: string;
  cipherText: string;
  updatedAt?: number;
};

type TerminalCheckpointPayloadV1 = {
  terminalLabels: string[];
};

type TerminalCheckpointScope = {
  seed: string;
  chainId: number;
  scope: bigint;
};

type SaveTerminalCheckpointParams = TerminalCheckpointScope & {
  terminalSet: Set<string>;
};

const normalizeSeed = (seed: string): string =>
  seed
    .replace(/[,\r\n]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getStorage = (): Storage | null => {
  try {
    if (typeof globalThis.localStorage === 'undefined') return null;
    
return globalThis.localStorage;
  } catch {
    return null;
  }
};

const getWebCrypto = (): Crypto | null => {
  if (typeof globalThis.crypto === 'undefined') return null;
  if (!globalThis.crypto.subtle) return null;
  
return globalThis.crypto;
};

const isValidBase64 = (value: string): boolean =>
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(
    value,
  );

const toBase64 = (bytes: Uint8Array): string => {
  if (typeof globalThis.btoa === 'function') {
    let binary = '';
    for (const value of bytes) binary += String.fromCharCode(value);
    
return globalThis.btoa(binary);
  }

  throw new Error('base64 encoder unavailable');
};

const fromBase64 = (value: string): Uint8Array => {
  if (!isValidBase64(value)) {
    throw new Error('invalid base64');
  }

  if (typeof globalThis.atob === 'function') {
    const binary = globalThis.atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    
return bytes;
  }

  throw new Error('base64 decoder unavailable');
};

const toHex = (bytes: Uint8Array): string =>
  [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

const sha256Hex = async (value: string, cryptoApi: Crypto): Promise<string> => {
  const digest = await cryptoApi.subtle.digest(
    'SHA-256',
    textEncoder.encode(value),
  );
  
return toHex(new Uint8Array(digest));
};

const getStorageKey = async (
  params: TerminalCheckpointScope,
  cryptoApi: Crypto,
): Promise<string> => {
  const ownerId = await sha256Hex(
    `${TERMINAL_OWNER_NAMESPACE}${normalizeSeed(params.seed)}`,
    cryptoApi,
  );
  const keyId = await sha256Hex(
    `${TERMINAL_KEY_NAMESPACE}${ownerId}:${
      params.chainId
    }:${params.scope.toString()}`,
    cryptoApi,
  );
  
return `${TERMINAL_CHECKPOINT_STORAGE_PREFIX}${keyId}`;
};

const deriveDataKey = async (
  seed: string,
  cryptoApi: Crypto,
): Promise<CryptoKey> => {
  const rawSeedDigest = await cryptoApi.subtle.digest(
    'SHA-256',
    textEncoder.encode(normalizeSeed(seed)),
  );
  const hkdfKey = await cryptoApi.subtle.importKey(
    'raw',
    rawSeedDigest,
    'HKDF',
    false,
    ['deriveKey'],
  );

  return cryptoApi.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: textEncoder.encode(TERMINAL_HKDF_SALT),
      info: textEncoder.encode(TERMINAL_HKDF_INFO),
    },
    hkdfKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
};

const parseEnvelope = (rawValue: string): TerminalCheckpointEnvelopeV1 => {
  const value = JSON.parse(
    rawValue,
  ) as Partial<TerminalCheckpointEnvelopeV1> | null;
  if (!value || typeof value !== 'object')
    throw new Error('invalid checkpoint envelope');
  if (value.version !== TERMINAL_CHECKPOINT_VERSION)
    throw new Error('checkpoint version mismatch');
  if (typeof value.iv !== 'string' || typeof value.cipherText !== 'string') {
    throw new Error('invalid checkpoint envelope');
  }
  if (
    value.updatedAt != null &&
    (!Number.isFinite(value.updatedAt) || value.updatedAt < 0)
  ) {
    throw new Error('invalid checkpoint envelope');
  }

  return {
    version: TERMINAL_CHECKPOINT_VERSION,
    iv: value.iv,
    cipherText: value.cipherText,
    updatedAt: value.updatedAt,
  };
};

const parsePayload = (rawPayload: string): TerminalCheckpointPayloadV1 => {
  const payload = JSON.parse(
    rawPayload,
  ) as Partial<TerminalCheckpointPayloadV1> | null;
  if (
    !payload ||
    typeof payload !== 'object' ||
    !Array.isArray(payload.terminalLabels)
  ) {
    throw new Error('invalid checkpoint payload');
  }
  if (payload.terminalLabels.some((label) => typeof label !== 'string')) {
    throw new Error('invalid checkpoint payload');
  }

  return {
    terminalLabels: payload.terminalLabels,
  };
};

const invalidateStorageKey = (storage: Storage, storageKey: string): void => {
  try {
    storage.removeItem(storageKey);
  } catch {
    // ignore storage failures to avoid blocking account loading
  }
};

export const loadTerminalCheckpoint = async (
  params: TerminalCheckpointScope,
): Promise<Set<string>> => {
  const storage = getStorage();
  const cryptoApi = getWebCrypto();
  if (!storage || !cryptoApi) return new Set();

  let storageKey: string;
  try {
    storageKey = await getStorageKey(params, cryptoApi);
  } catch {
    return new Set();
  }

  let rawEnvelope: string | null;
  try {
    rawEnvelope = storage.getItem(storageKey);
  } catch {
    return new Set();
  }
  if (!rawEnvelope) return new Set();

  try {
    const envelope = parseEnvelope(rawEnvelope);
    const iv = fromBase64(envelope.iv);
    if (iv.length !== TERMINAL_CHECKPOINT_IV_BYTES) {
      throw new Error('invalid checkpoint iv');
    }
    const cipherBytes = fromBase64(envelope.cipherText);

    const dataKey = await deriveDataKey(params.seed, cryptoApi);
    const plainBuffer = await cryptoApi.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData: textEncoder.encode(storageKey),
      },
      dataKey,
      cipherBytes,
    );
    const payload = parsePayload(textDecoder.decode(plainBuffer));
    
return new Set(payload.terminalLabels);
  } catch {
    invalidateStorageKey(storage, storageKey);
    
return new Set();
  }
};

export const saveTerminalCheckpoint = async (
  params: SaveTerminalCheckpointParams,
): Promise<void> => {
  const storage = getStorage();
  const cryptoApi = getWebCrypto();
  if (!storage || !cryptoApi) return;

  try {
    const storageKey = await getStorageKey(params, cryptoApi);
    const dataKey = await deriveDataKey(params.seed, cryptoApi);
    const iv = cryptoApi.getRandomValues(
      new Uint8Array(TERMINAL_CHECKPOINT_IV_BYTES),
    );
    const payload: TerminalCheckpointPayloadV1 = {
      terminalLabels: [...params.terminalSet],
    };
    const cipherBuffer = await cryptoApi.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv,
        additionalData: textEncoder.encode(storageKey),
      },
      dataKey,
      textEncoder.encode(JSON.stringify(payload)),
    );

    const envelope: TerminalCheckpointEnvelopeV1 = {
      version: TERMINAL_CHECKPOINT_VERSION,
      iv: toBase64(iv),
      cipherText: toBase64(new Uint8Array(cipherBuffer)),
      updatedAt: Date.now(),
    };
    storage.setItem(storageKey, JSON.stringify(envelope));
  } catch {
    // best-effort write only; never block account reconstruction
  }
};
